import type { FiltersConfigState } from '@reducers/filtersConfig';
import type { SearchState } from '@reducers/search';
import {
  CategoryID,
  FilterURI,
  SavedSearchBase,
  SavedSearchFilter,
} from '@sbt-web/network/types';
import { TuttaItalia } from '@sbt-web/networking';
import { FiltersStore } from '@tools/search/getFiltersStore';

export function mapToSavedSearch(
  search: SearchState,
  filtersConfig: FiltersConfigState
): SavedSearchBase {
  const filters: SavedSearchFilter[] = [];
  const sort = search.category.orders[search.orderIndex];
  const currentConfig = FiltersStore.fromSerialized(filtersConfig.filtersStore);

  filters.push({
    uri: FilterURI.AdType,
    values: [{ key: search.adType }],
  });

  if (search.query) {
    filters.push({
      uri: FilterURI.Query,
      values: [{ key: search.query }],
    });

    if (search.qso) {
      filters.push({
        uri: FilterURI.QuerySubjectOnly,
        values: [{ key: 'true' }],
      });
    }
  }

  if (search.includeShippableOnly) {
    filters.push({
      uri: FilterURI.ItemShippable,
      values: [{ key: 'true' }],
    });
  }

  if (search.includeUrgent) {
    filters.push({
      uri: FilterURI.ItemUrgent,
      values: [{ key: 'true' }],
    });
  }

  if (search.category.id != CategoryID.Tutte) {
    filters.push({
      uri: FilterURI.Category,
      values: [{ key: search.category.id }],
    });
  }

  if (search.radiusSearch) {
    filters.push({
      uri: FilterURI.Radius,
      values: [{ key: String(search.radiusSearch.radiusMeters) }],
    });
    filters.push({
      uri: FilterURI.Latitude,
      values: [{ key: String(search.radiusSearch.center.lat) }],
    });
    filters.push({
      uri: FilterURI.Longitude,
      values: [{ key: String(search.radiusSearch.center.lng) }],
    });
  } else {
    if (search.geo.region.id != TuttaItalia.id) {
      const region = search.geo.region;
      if (search.searchNearRegions && region.neighbors) {
        filters.push({
          uri: region.uri,
          values: [{ key: region.neighbors }],
        });
      } else {
        filters.push({
          uri: region.uri,
          values: [{ key: region.id }],
        });
      }

      if (search.geo.city) {
        const city = search.geo.city;
        const uri = city.uri;
        if (!uri) {
          console.error('[Geo URI]: City URI undefined');
        }
        filters.push({
          uri,
          values: [{ key: city.id }],
        });
      }

      if (search.geo.town) {
        const town = search.geo.town;
        const uri = town.uri;
        if (!uri) {
          console.error('[Geo URI]: Town URI undefined');
        }
        filters.push({
          uri,
          values: [{ key: town.id }],
        });
      }

      if (search.geo.zone) {
        const { zone } = search.geo;
        const uri = zone.uri;
        if (!uri) {
          console.error('[Geo URI]: Zone URI undefined');
        }
        filters.push({
          uri,
          values: [{ key: zone.id }],
        });
      }
    }
  }

  if (currentConfig) {
    for (const short in search.filters) {
      const value = search.filters[short];
      const filter = currentConfig.findByFilterQueryString(short);

      if (value && filter?.uri) {
        if (filter.uri == '/advertiser_type') {
          filters.push({
            uri: filter.uri,
            values: [{ key: value }],
          });
        } else {
          filters.push({
            uri: filter.uri as FilterURI,
            values: value.split(',').map((value) => ({ key: value })), // Values can be comma separated (like for multi-selection filters)
          });
        }
      }
    }
  }

  return { sort, filters };
}
